/**
 * Maths on screen: MathMarkup rendered as real typography — italic
 * variables, upright numbers and function names, stacked fractions,
 * raised/lowered scripts, a root sign with an overline, the U+2212 minus, and
 * the answer blank as a rounded dashed box that gently pulses.
 *
 * Screen readers get the spoken form (core toSpeech) as the label instead of
 * the individual glyphs.
 */
import { memo, useMemo, type CSSProperties, type ReactNode, type Ref } from 'react';
import type { Locale, MathMarkup, Problem } from '@dropstack/core';
import { instructionParts } from './instruction.ts';
import { markupView, speechFor, type MathView } from './mathView.ts';

function renderNodes(nodes: readonly MathView[]): ReactNode[] {
  return nodes.map((node, i) => renderNode(node, i));
}

function renderNode(node: MathView, key: number): ReactNode {
  switch (node.kind) {
    case 'num':
      return (
        <span key={key} className="m-num">
          {node.text}
        </span>
      );
    case 'var':
      return (
        <i key={key} className="m-var">
          {node.text}
        </i>
      );
    case 'op':
      return (
        <span key={key} className="m-op">
          {node.text}
        </span>
      );
    case 'fn':
      return (
        <span key={key} className="m-fn">
          {node.text}
        </span>
      );
    case 'text':
      return (
        <span key={key} className="m-text">
          {node.text}
        </span>
      );
    case 'sym':
      return (
        <span key={key} className="m-sym">
          {node.text}
        </span>
      );
    case 'space':
      return <span key={key} className="m-space" />;
    case 'sep':
      return <span key={key} className="m-sep" />;
    case 'box':
      return node.fill ? (
        <span key={key} className="m-box m-box--filled">
          {renderNodes(node.fill)}
        </span>
      ) : (
        <span key={key} className="m-box" />
      );
    case 'sup':
      return (
        <span key={key} className="m-sup">
          {renderNodes(node.children)}
        </span>
      );
    case 'sub':
      return (
        <span key={key} className="m-sub">
          {renderNodes(node.children)}
        </span>
      );
    case 'subsup':
      return (
        <span key={key} className="m-subsup">
          <span className="m-subsup__sup">{renderNodes(node.sup)}</span>
          <span className="m-subsup__sub">{renderNodes(node.sub)}</span>
        </span>
      );
    case 'frac':
      return (
        <span key={key} className="m-frac">
          <span className="m-frac__num">{renderNodes(node.num)}</span>
          <span className="m-frac__den">{renderNodes(node.den)}</span>
        </span>
      );
    case 'sqrt':
      return (
        <span key={key} className="m-sqrt">
          {node.index && <span className="m-sqrt__index">{renderNodes(node.index)}</span>}
          <span className="m-sqrt__sign" aria-hidden="true">
            √
          </span>
          <span className="m-sqrt__body">{renderNodes(node.body)}</span>
        </span>
      );
  }
}

export interface MathNodesProps {
  nodes: readonly MathView[];
  /** Spoken form; without it the maths is hidden from screen readers (the caller labels it). */
  label?: string;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLSpanElement>;
}

/** Render an already-built view tree (answer reveals, choice values). */
export const MathNodes = memo(function MathNodes({ nodes, label, className, style, ref }: MathNodesProps) {
  const classes = ['math', className].filter(Boolean).join(' ');
  return label ? (
    <span ref={ref} className={classes} style={style} role="math" aria-label={label}>
      {renderNodes(nodes)}
    </span>
  ) : (
    <span ref={ref} className={classes} style={style} aria-hidden="true">
      {renderNodes(nodes)}
    </span>
  );
});

export interface MathTextProps {
  markup: MathMarkup;
  locale: Locale;
  /** Draw the answer blank filled with this (e.g. the answer, after a correct answer). */
  fill?: MathView[] | null;
  /** Hide from screen readers (when the surrounding element already says it). */
  silent?: boolean;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<HTMLSpanElement>;
}

/** MathMarkup → typeset maths, labelled with its spoken form. */
export const MathText = memo(function MathText({ markup, locale, fill = null, silent = false, className, style, ref }: MathTextProps) {
  const nodes = useMemo(() => markupView(markup, { fill }), [markup, fill]);
  const label = useMemo(() => (silent ? undefined : speechFor(markup, locale)), [markup, locale, silent]);
  return <MathNodes ref={ref} nodes={nodes} label={label} className={className} style={style} />;
});

export interface InstructionLineProps {
  instruction: Problem['instruction'];
  locale: Locale;
  className?: string;
  style?: CSSProperties;
}

/** "Find the value if x = 3": text from core instructionText(), *Math params typeset as maths. */
export const InstructionLine = memo(function InstructionLine({ instruction, locale, className, style }: InstructionLineProps) {
  const parts = useMemo(() => instructionParts(instruction, locale), [instruction, locale]);
  if (!parts.length) return null;
  return (
    <span className={['instruction', className].filter(Boolean).join(' ')} style={style}>
      {parts.map((part, i) =>
        part.kind === 'text' ? (
          <span key={i}>{part.text}</span>
        ) : (
          <MathText key={i} markup={part.markup} locale={locale} className="instruction__math" />
        ),
      )}
    </span>
  );
});
